import { ENDPOINT } from '~/constants';

import { type UUID } from '~/types/common';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type SupplierRequestActionBody } from './types';

/**
 * Resolves a pending supplier request by creating a new company, linking to an
 * existing one, or rejecting the request.
 *
 * @param reqId - The UUID of the supplier request to process.
 * @param body - The action payload (create / link / reject).
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Supplier-Requests/operation/process_request_vadmin_supplier_requests__req_id__patch
 */
export const processSupplierRequest = async (
  reqId: UUID,
  body: SupplierRequestActionBody,
) => {
  try {
    await vestigasApi
      .patch(ENDPOINT.SUPPLIER_REQUEST.PROCESS(reqId), { json: body })
      .json();
  } catch (error) {
    log.error({
      message: 'Error processing supplier request',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(($) => $.supplierRequest.process.error422, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.supplierRequest.process.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    throw error;
  }
};
